import * as React from "react";
import { useEffect, useState } from "react";

import { SummaryState } from "../TreePicklistItemProvider";

import { ArrowIcon, FieldSet, SingleNodeLabel } from "./TreeNode.styles";
import { CommonTreeNodeProps } from "./utils/ITreeNodeProps";
import { useNodeLabelName } from "./utils/useNodeLabelName";
import { useUpdateNodes } from "./utils/useUpdateNodes";
import { ShowMoreButton, useShowingMoreButton } from "./ShowMoreButton";
import { Preloader } from "./Preloader";
import { useChildrenNodesNeedToOpen } from "./utils/useChildrenNodesNeedToOpen";

type SingleTreeNodeProps = CommonTreeNodeProps;

export const SingleTreeNode = (props: SingleTreeNodeProps) => {
    const { item } = props;
    const [summaryOpened, setSummaryOpened] = useState(false);

    const labelName = useNodeLabelName(props.display, item);

    const [isPicked, setIsPicked] = useState<boolean>(false);

    const childrenNodesNeedToOpen = useChildrenNodesNeedToOpen(item.elementId, props.needNodesToOpen);

    const { children, loading } = useUpdateNodes({
        props: props,
        summaryOpened: summaryOpened,
        onLoadingError: props.onLoadingError,
        nodesNeedToOpen: childrenNodesNeedToOpen,
        searchMode: props.searchMode,
    });

    const searchChildrenCount = childrenNodesNeedToOpen.length;
    const childrenCount = props.searchMode ? searchChildrenCount : Number(item.childrenCount);

    useEffect(() => {
        if (props.needNodesToOpen.some(itemToOpen => itemToOpen.elementId === item.elementId)) {
            setSummaryOpened(true);

            const containValue = props.value.split(", ").includes(item.elementId);
            if (containValue) {
                setIsPicked(true);
            }
        }
    }, [props.needNodesToOpen]);

    const toggleHandler = () => {
        setSummaryOpened(prevState => !prevState);
    };

    const canPickItem = checkIsPickAllowed(props);

    const pickHandler = () => {
        if (canPickItem) {
            props.updateNodesToSave(item, SummaryState.Checked);
        }
    };

    const showingMoreButton = useShowingMoreButton(props, childrenCount) && !loading;
    const showingPreloader = loading;

    const hasTreePicklistNodesToOpen = Boolean(props.needNodesToOpen.length);
    const isCurrentNodeNeedsToBeOpened = props.needNodesToOpen.some(
        itemToOpen => itemToOpen.elementId === item.elementId
    );
    const shouldSkipNode = props.searchMode && hasTreePicklistNodesToOpen && !isCurrentNodeNeedsToBeOpened;

    if (shouldSkipNode) {
        return null;
    }

    if (item.isFinalNode) {
        //Лист в дереве, конечный элемент
        return (
            <SingleNodeLabel isPicked={isPicked} withHover={canPickItem} isLeaf={true} onClick={pickHandler}>
                {labelName}
            </SingleNodeLabel>
        );
    }

    return (
        <>
            <div style={{ margin: "8px 0", display: "flex" }}>
                <ArrowIcon opened={summaryOpened} onClick={toggleHandler} />
                <SingleNodeLabel isPicked={isPicked} withHover={canPickItem} isLeaf={false} onClick={pickHandler}>
                    {labelName}
                </SingleNodeLabel>
            </div>
            {summaryOpened && (
                <FieldSet>
                    {children.map(childItem => (
                        <SingleTreeNode
                            {...props}
                            key={childItem.elementId}
                            item={childItem}
                            nestingLevel={props.nestingLevel + 1}
                            needNodesToOpen={props.needNodesToOpen}
                            onLoadingError={props.onLoadingError}
                            updateNodesToSave={props.updateNodesToSave}
                        />
                    ))}
                    {showingMoreButton && <ShowMoreButton elementId={item.elementId} childrenCount={childrenCount} />}
                    {showingPreloader && <Preloader />}
                </FieldSet>
            )}
        </>
    );
};

const checkIsPickAllowed = (props: SingleTreeNodeProps): boolean => {
    const isLevelInAllowedGroup = checkIsLevelInAllowedGroup(props);
    const isLevelInAllowedScope = checkIsLevelInAllowedScope(props);
    const isAllowedByDisablingField = checkIsAllowedByDisablingField(props);

    const isPickAllowed = isLevelInAllowedGroup && isLevelInAllowedScope && isAllowedByDisablingField;

    return isPickAllowed;
};

function checkIsLevelInAllowedGroup(props: SingleTreeNodeProps): boolean {
    return props.allowedToPickLevels ? Boolean(props.allowedToPickLevels.includes(props.nestingLevel)) : true;
}

function checkIsLevelInAllowedScope(props: SingleTreeNodeProps): boolean {
    return props.nestingLevel >= props.minPickLevel;
}

function checkIsAllowedByDisablingField(props: SingleTreeNodeProps): boolean {
    return props.fieldToDisableElementPick
        ? !parseDisablingFieldValue(props.item.toOriginal()[props.fieldToDisableElementPick])
        : true;
}

const parseDisablingFieldValue = (value?: string | number): boolean => {
    if (typeof value === "string") {
        return value.toLowerCase() === "true";
    }

    return Boolean(value);
};
